import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import { NotificationProvider } from './context/NotificationContext';
import NotificationsUI from './components/NotificationsUI';
import InstallPrompt from './components/InstallPrompt';
import { loadAndInjectFontCss } from './utils/fontSettings';
import ProtectedRoute from './components/ProtectedRoute';
import StaffRoute from './components/StaffRoute';
import Home from './pages/Home';
import './App.css';

// Lazy loading for better performance
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const ProductIntroductionDetail = lazy(() => import('./pages/ProductIntroductionDetail'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Profile = lazy(() => import('./pages/Profile'));
const Orders = lazy(() => import('./pages/Orders'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const Chat = lazy(() => import('./pages/Chat'));
const ChatsList = lazy(() => import('./pages/ChatsList'));
const StaffChat = lazy(() => import('./pages/StaffChat'));
const StaffAnalytics = lazy(() => import('./pages/StaffAnalytics'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const StaffSettlement = lazy(() => import('./pages/StaffSettlement'));
const CourierPanel = lazy(() => import('./pages/CourierPanel'));
const CategoriesPage = lazy(() => import('./pages/CategoriesPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const Contact = lazy(() => import('./pages/Contact'));
const About = lazy(() => import('./pages/About'));
const PartnerRegister = lazy(() => import('./pages/PartnerRegister'));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-kadolar-500"></div>
  </div>
);

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return null;
};

const TermsPage = () => {
  const { systemSettings } = useSettings();
  const [loading, setLoading] = React.useState(true);
  const [content, setContent] = React.useState('');
  const [title, setTitle] = React.useState('قوانین و مقررات');
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { supportAPI } = await import('./services/api');
        const resp = await supportAPI.getActivePurchaseTerms();
        const data = resp?.data || {};
        const raw = String(data?.content || '').trim();
        if (mounted && raw) {
          setContent(raw);
          setTitle(String(data?.title || 'قوانین و مقررات') || 'قوانین و مقررات');
          setLoading(false);
          return;
        }
      } catch (_) {}
      if (!mounted) return;
      const fallback = String(systemSettings?.terms_and_conditions || '').trim();
      setContent(fallback);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [systemSettings?.terms_and_conditions]);

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">{title}</h1>
      {loading ? (
        <div className="bg-white rounded-3xl shadow p-6 text-gray-700">در حال بارگذاری...</div>
      ) : content ? (
        <div
          className="bg-white rounded-3xl shadow p-6 leading-8 text-gray-800 kadolar-font-terms"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      ) : (
        <div className="bg-white rounded-3xl shadow p-6 text-gray-700">
          قوانین و مقررات هنوز ثبت نشده است.
        </div>
      )}
    </div>
  );
};

const PrivacyPage = () => {
  const { systemSettings } = useSettings();
  const content = String(systemSettings?.privacy_policy || '').trim();
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">حریم خصوصی</h1>
      {content ? (
        <div
          className="bg-white rounded-3xl shadow p-6 leading-8 text-gray-800 kadolar-font-terms"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      ) : (
        <div className="bg-white rounded-3xl shadow p-6 text-gray-700">
          سیاست حریم خصوصی هنوز ثبت نشده است. در صورت نیاز، اطلاعات تماس خود را از صفحه «تماس با ما» مشاهده کنید.
        </div>
      )}
    </div>
  );
};

function App() {
  React.useEffect(() => {
    loadAndInjectFontCss();
  }, []);

  // اپ نصب‌شده (PWA) روی حالت عمودی قفل شود. `manifest.orientation` کافی نیست:
  // برخی مرورگرها/نسخه‌های iOS آن را نادیده می‌گیرند، پس اینجا هم قفل می‌کنیم.
  React.useEffect(() => {
    try {
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      if (!standalone) return;
      const so = window.screen && window.screen.orientation;
      if (!so || typeof so.lock !== 'function') return;
      so.lock('portrait').catch(() => {
        // مرورگر اجازه نداد (مثلاً خارج از fullscreen) — بی‌صدا رد می‌شویم
      });
    } catch (_) {
      /* بدون خطا */
    }
  }, []);

  return (
    <AuthProvider>
      <SettingsProvider>
        <NotificationProvider>
        <Router>
          <ScrollToTop />
          <div className="App">
            <NotificationsUI />
              <InstallPrompt />
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/introductions/:slug" element={<ProductIntroductionDetail />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />
              <Route path="/partner/register" element={
                <ProtectedRoute>
                  <PartnerRegister />
                </ProtectedRoute>
              } />
              <Route path="/orders" element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              } />
              <Route path="/wishlist" element={
                <ProtectedRoute>
                  <Wishlist />
                </ProtectedRoute>
              } />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<About />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              {/* مسیر پایه چت برای هدایت به لیست گفتگوها */}
              <Route path="/chat" element={
                <ProtectedRoute>
                  <ChatsList />
                </ProtectedRoute>
              } />
              <Route path="/chats" element={
                <ProtectedRoute>
                  <ChatsList />
                </ProtectedRoute>
              } />
              <Route path="/chat/:roomId" element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              } />
              {/* پنل مدیریت کارکنان */}
              <Route path="/staff" element={
                <StaffRoute allowedRoles={["support", "admin"]}>
                  <AdminPanel />
                </StaffRoute>
              } />
              {/* مسیرهای کارکنان */}
              <Route path="/staff/chat" element={
                <StaffRoute allowedRoles={["support", "admin"]}>
                  <StaffChat />
                </StaffRoute>
              } />
              <Route path="/staff/analytics" element={
                <StaffRoute allowedRoles={["support", "admin"]}>
                  <StaffAnalytics />
                </StaffRoute>
              } />
              <Route path="/staff/notifications" element={
                <StaffRoute allowedRoles={["support", "admin"]}>
                  <NotificationSettings />
                </StaffRoute>
              } />
              {/* P1 (بند ۲.۴): تسویه مختص پرسنل نیست — هر نقش همکار (فروشنده/
                  پیک/پیکی) حساب بانکی و درخواست تسویه دارد؛ لیست سمت API به
                  «درخواست‌های خودم» اسکوپ می‌شود. */}
              <Route path="/staff/settlement" element={
                <StaffRoute allowedRoles={["admin", "seller", "courier", "pike"]}>
                  <StaffSettlement />
                </StaffRoute>
              } />
              {/* P2 (بند ۳.۱): پنل پیک/پیکی — سه آکاردئونِ عملیات پیک که تا این‌جا
                  فقط داخل AdminPanel (مسیر support/admin) وجود داشتند و پیک‌ها هیچ UI
                  نداشتند. support/admin هم در فهرست است تا دسترسی قبلیشان نشکند. */}
              <Route path="/staff/courier" element={
                <StaffRoute allowedRoles={["courier", "pike", "admin", "support"]}>
                  <CourierPanel />
                </StaffRoute>
              } />
              </Routes>
            </Suspense>
          </div>
        </Router>
        </NotificationProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
